/**
 * datevMapping — VB-1-Slice: Steuerkategorie → SKR-Konto + BU-/Steuerschlüssel.
 *
 * Reine, deterministische Lookup-Tabelle für den DATEV-Buchungsstapel-Export (M7).
 * KEINE Firebase-/React-Imports — voll unit-testbar (Jest, Soll-Paare §4.3 architecture-m7.md).
 *
 * Gate-Status (e-rechnung-Gate, solar-m7-datev-ruling.md):
 *   - MVP-safe, FREIGEGEBEN: Erlös 19 % (8400/4400), Erlös 7 % (8300/4300),
 *     §19-Kleinunternehmer (8195/4185, Pflichtkonto seit 01.01.2025), Vorsteuer 19 % (BU 9),
 *     Vorsteuer 7 % (BU 8), §19-Lieferant/steuerfrei (kein VSt).
 *   - needs-confirm → fail-closed Bucket (Funktion liefert `null`): §4-steuerfrei/§6a-IGL/
 *     §13b-out/§25a/Ausfuhr-Erlöse, §13b/IGL/Drittland/needs_review-Ausgaben.
 *
 * Auflage A5 (Automatikkonto×BU-Schlüssel-Kollision): Ein BU-Schlüssel auf einem vom
 * Nutzer/KI gesetzten `skrAccount` ist ein DATEV-Konflikt (das Konto kann ein
 * steuerführendes Automatikkonto sein). Deshalb: BU 8/9 wird AUSSCHLIESSLICH auf dem
 * von M7 selbst gesetzten, nicht-automatischen Default-Aufwandskonto (4980/6300) emittiert.
 * Auf einem nutzergesetzten `skrAccount` bleibt das BU-Feld LEER.
 *
 * ─── WL-K4-Traceability (BU-Schlüssel-Vollständigkeit + Fail-closed, modules-loop-b.md) ──
 *
 * IST-Audit (2026-07-07): jede Zeile der WL-K4-Falltabelle ist bereits abgedeckt — die
 * Automatikkonto-Architektur (Auflage A5/Auflage-1 oben) macht die meisten Zeilen trivial
 * richtig (Automatikkonto kodiert den Steuersatz bereits im Kontonamen ⇒ `buKey` MUSS
 * `null` bleiben, sonst Doppel-Steuerwirkung). Mapping-Herkunft je Falltabellen-Zeile:
 *
 *   | WL-K4-Fall                         | Ergebnis                    | Quelle/Codepfad |
 *   |-------------------------------------|------------------------------|------------------|
 *   | S 19 % (Erlös)                      | Konto 8400/4400, `buKey=null`| `mapRevenueAccount` Standard-Zweig, Automatikkonto |
 *   | S 7 % (Erlös)                       | Konto 8300/4300, `buKey=null`| dito |
 *   | S 19 %/7 % (Aufwand, VSt abziehbar, Default-Konto) | Konto 4980/6300, `buKey='9'`/`'8'` | `mapExpenseAccount`, `VORSTEUER_BU` |
 *   | Z Nullsatz eindeutig (pv_12_3)       | Automatikkonto 8290/4290, `buKey=null` | `DATEV_VB1_REVENUE_ACCOUNTS.pv_12_3` |
 *   | Z Nullsatz mehrdeutig                | `null` → Bucket `zeroRateAmbiguous` | `datevExport.ts::processInvoice` |
 *   | AE §13b Ausgang (Erlös)              | Automatikkonto 8337/4337, `buKey=null` | `DATEV_VB1_REVENUE_ACCOUNTS.reverse_charge_13b` |
 *   | §13b Eingangsseite (Aufwand, KZ 46/47) | `null` → Bucket `taxSchemeNeedsConfirm` | `EXPENSE_BUCKET_SCHEMES` (bewusst NICHT verlassen — s. u.) |
 *   | §13b `_mixed` (AE+S koexistieren)    | kein erzwungener Schlüssel (pro Steuersatz eigene Zeile/eigenes Automatikkonto — keine Beleg-weite Entscheidung nötig) | `perRate[]`-Architektur in `datevExport.ts` |
 *   | E steuerfrei §4 (Erlös, exempt_4)    | Automatikkonto 8100/4100, `buKey=null` | `DATEV_VB1_REVENUE_ACCOUNTS.exempt_4` |
 *   | E/zero_exempt (Aufwand)              | Default-/Nutzerkonto, `buKey=null` (kein VSt) | `mapExpenseAccount` Fallthrough |
 *   | K §6a IGL-Lieferung (Erlös)          | Automatikkonto 8125/4125 NUR mit Käufer-USt-IdNr., sonst Bucket | `mapVb1RevenueSelection` Auflage 2 |
 *   | K §6a Erwerb (Aufwand, `intra_community_6a`) | `null` → Bucket `taxSchemeNeedsConfirm` | `EXPENSE_BUCKET_SCHEMES` (bewusst NICHT verlassen — s. u.) |
 *   | §25a Differenzbesteuerung            | nicht als eigene `InvoiceTaxCategoryKey`/`TaxScheme` modelliert → jeder Versuch (z. B. `selectionKey='differential_taxation_25a'`) fällt automatisch in den Residual-Bucket | `resolveRevenueSelection` Catch-all (getestet: `datevMapping.test.ts` „unknown_selectionKey_oss") |
 *   | §19 Kleinunternehmer (Erlös + Aufwand)| Automatikkonto 8195/4185 bzw. Default-/Nutzerkonto, `buKey=null` (kein USt-Schlüssel) | `DATEV_REVENUE_ACCOUNTS.smallBusiness` / `mapExpenseAccount` |
 *   | G Ausfuhr (Erlös, export_third_country) | Automatikkonto 8120/4120, `buKey=null` | `DATEV_VB1_REVENUE_ACCOUNTS.export_third_country` |
 *   | unbekannt/kein Mapping                | `null` → sichtbarer Ausschluss-Bucket | Residual-Zweige beider Funktionen |
 *
 * Bewusst NICHT gesetzt (konservativ, [E-RECHNUNG]-Gate-Vorlage statt Rate-Versuch):
 *   - **§13b Eingangsseite** (`reverse_charge_13b` als Aufwand): DATEV kennt je nach
 *     §13b-Abs.-2-Untertatbestand (Bauleistungen, Schrott/Metalle, Gas/Strom/Wärme,
 *     Emissionszertifikate, Telekommunikation, Gebäudereinigung, Werklieferung eines
 *     ausl. Unternehmers, …) UNTERSCHIEDLICHE BU-Schlüssel. Der aktuelle `TaxScheme`
 *     kennt nur EINEN generischen `reverse_charge_13b`-Wert — ohne den Untertatbestand
 *     wäre jeder einzelne konkrete Schlüssel für die anderen Untertatbestände falsch,
 *     aber plausibel. Bleibt im Ausschluss-Bucket (unverändert ggü. IST).
 *   - **§6a innergem. Erwerb** (`intra_community_6a` als Aufwand): in der Fachliteratur
 *     kursieren dafür die BU-Schlüssel 17 (7 %) / 18 (19 %) — DIESES Repo hat dafür
 *     bisher KEINE verifizierte Quelle (kein Gate-Ruling, kein Fixture-Beleg). Da eine
 *     falsche Ziffer hier eine echte Doppel-/Falschbuchung der Erwerbsteuer wäre, bleibt
 *     der Fall konservativ im Ausschluss-Bucket. **Kandidat für [E-RECHNUNG]-Bestätigung**
 *     (danach: `EXPENSE_BUCKET_SCHEMES` um `intra_community_6a` reduzieren + `buKey`
 *     analog zu `VORSTEUER_BU` ergänzen).
 *
 * @module datevMapping
 */

import type { TaxScheme } from '../types/expenseTypes';
import type { InvoiceTaxCategoryKey } from './taxCategoryCatalog';

/** Wählbarer DATEV-Standardkontenrahmen. */
export type SkrKontenrahmen = 'SKR03' | 'SKR04';

/**
 * Ergebnis eines Kontierungs-Lookups.
 * `null` (statt eines AccountMapping) bedeutet: fail-closed Bucket (§4.6) — NIE geraten.
 */
export interface AccountMapping {
  /** Sachkonto (Erlös- bzw. Aufwandskonto). 4-stellig (Sachkontenlänge 4). */
  account: string;
  /** BU-/Steuerschlüssel oder `null` (Automatikkonto / kein Schlüssel). */
  buKey: string | null;
  /** Gate-Markierung — alle hier zurückgegebenen Mappings sind freigegeben. */
  gateStatus: 'mvp-safe' | 'needs-confirm';
}

// ─── SoT-Konstanten (gegen Literal-Drift) ───────────────────────────────────────

/**
 * Erlös-Sachkonten je Steuersatz und Kontenrahmen (e-rechnung-Gate freigegeben).
 * Single-Source-of-Truth — NIE Literale an der Call-Site duplizieren.
 */
export const DATEV_REVENUE_ACCOUNTS = {
  SKR03: { rate19: '8400', rate7: '8300', smallBusiness: '8195' },
  SKR04: { rate19: '4400', rate7: '4300', smallBusiness: '4185' },
} as const;

/**
 * Erlöskonten der expliziten VB-1-Steuerkategorien (Nicht-Standard) je Kontenrahmen.
 *
 * Verbindliche Quelle: `solar-vb1-revenue-accounts.md` (e-rechnung-Gate ⚠️MINOR,
 * 2026-06-26). Seit dem VB-1-Release wählt der Aussteller die Steuerart **explizit**
 * (`taxCategorySelection`), deshalb dürfen diese Kategorien jetzt deterministisch aufs
 * Erlöskonto mappen statt fail-closed zu bucketn.
 *
 * 🔴 Auflage 1 (VETO-Gefahr): ALLE diese Konten sind DATEV-**Automatik-Konten** — der
 * Steuersatz/die Steuerfreiheit ist im Konto fest hinterlegt. Es darf NIEMALS ein
 * BU-/Steuerschlüssel auf diese Konten gesetzt werden (ein 19-%-Schlüssel auf einem
 * 0-%-Konto = stille Fehlbuchung → falsche UStVA). Das Mapping liefert daher stets
 * `buKey: null` (siehe `mapVb1RevenueSelection`). NIE ändern.
 *
 * `standard` ist bewusst NICHT enthalten — Standard (S, 19/7 %) läuft weiter über die
 * satzbasierte Logik in `mapRevenueAccount` (`DATEV_REVENUE_ACCOUNTS`), die als einzige
 * (für 8400/8300) einen BU-Schlüssel tragen darf.
 */
export const DATEV_VB1_REVENUE_ACCOUNTS: Record<
  Exclude<InvoiceTaxCategoryKey, 'standard'>,
  { SKR03: string; SKR04: string }
> = {
  pv_12_3: { SKR03: '8290', SKR04: '4290' }, // Erlöse 0 % USt (PV §12 Abs. 3)
  small_business_19: { SKR03: '8195', SKR04: '4185' }, // §19 Kleinunternehmer
  exempt_4: { SKR03: '8100', SKR04: '4100' }, // steuerfrei §4 Nr. 8 ff. (o. VSt)
  intra_community_6a: { SKR03: '8125', SKR04: '4125' }, // i.g. Lieferung §4 Nr. 1b
  reverse_charge_13b: { SKR03: '8337', SKR04: '4337' }, // §13b Ausgang
  export_third_country: { SKR03: '8120', SKR04: '4120' }, // Ausfuhr §4 Nr. 1a
} as const;

/**
 * Eindeutige BT-151-Kategorie-Codes → VB-1-Auswahl-Schlüssel.
 *
 * Fallback, falls die Rechnung nur den BT-151-Code (API-/Altpfad) trägt, nicht aber den
 * präzisen `taxCategorySelection`. `S/Z/K/AE/G` sind je Code eindeutig einer Auswahl
 * zuordenbar. `E` ist bewusst NICHT enthalten — es ist mehrdeutig (§19 vs. §4) und darf
 * ohne `taxCategorySelection` NIE geraten werden (fail-closed → Bucket).
 */
const BT151_CODE_TO_SELECTION: Readonly<Record<string, InvoiceTaxCategoryKey>> = {
  S: 'standard',
  Z: 'pv_12_3',
  K: 'intra_community_6a',
  AE: 'reverse_charge_13b',
  G: 'export_third_country',
} as const;

/**
 * Generisches Default-Aufwandskonto „sonstige betriebliche Aufwendungen"
 * (Orchestrator-Produktentscheidung: Default-Konto statt Bucket bei fehlendem `skrAccount`).
 * Es ist ein NICHT-Automatikkonto → auf ihm wird der Vorsteuer-BU-Schlüssel (A5) gesetzt.
 */
export const DATEV_DEFAULT_EXPENSE_ACCOUNT = {
  SKR03: '4980',
  SKR04: '6300',
} as const;

/**
 * Sammel-Personenkonten (Gegenkonto). 5-stellig (= Sachkontenlänge 4 + 1, A6).
 * MVP nutzt ein Sammel-Debitoren- und ein Sammel-Kreditorenkonto; der Personen-Name
 * steht im Buchungstext (Q-4: Sammelkonto im MVP fachlich akzeptabel).
 */
export const DATEV_COLLECTIVE_ACCOUNTS = {
  /** Debitoren-Sammelkonto (Gegenkonto für Ausgangsrechnungen/Erlöse). */
  debtor: '10000',
  /** Kreditoren-Sammelkonto (Gegenkonto für Ausgaben/Aufwand). */
  creditor: '70000',
} as const;

/** Vorsteuer-BU-Schlüssel (DATEV-Standard, e-rechnung-Gate bestätigt). */
const VORSTEUER_BU = {
  rate19: '9', // Vorsteuer 19 %
  rate7: '8', // Vorsteuer 7 %
} as const;

/**
 * Privatentnahme-Sachkonten je Kontenrahmen (Expense-Storno-Feature, Variante B).
 *
 * Single-Source-of-Truth für das Gegenkonto einer „Umbuchung auf Privatentnahme"-
 * Korrektur (§12 Nr. 1 EStG / §15 Abs. 1a UStG). Fachlich belegt: Recherche §1c
 * (Fußnote) + BuchhaltungsButler-Quelle (architecture-expense-storno.md §5.2).
 *
 * Verwendung: `buildCorrectionInput` (`utils/expenseCorrection.ts`) löst diesen
 * Wert für `correctionType==='privatentnahme'` auf und schreibt ihn auf
 * `Expense.privatentnahmeAccount`; `mapExpenseToDatevInput` (`datev/datevSource.ts`)
 * liest ihn zurück in `DatevExpenseInput.gegenkontoOverride`, das `processExpense`
 * (`datevExport.ts`) statt des Kreditor-Sammelkontos als Gegenkonto setzt.
 *
 * KEIN Automatikkonto-Charakter (anders als `DATEV_VB1_REVENUE_ACCOUNTS`) — dies
 * ist ein reines Gegenkonto, der BU-Schlüssel wird weiterhin vom Aufwandskonto
 * (`mapExpenseAccount`) über den Reversal-Pfad bestimmt.
 */
export const DATEV_PRIVATENTNAHME_ACCOUNTS: Readonly<Record<SkrKontenrahmen, string>> = {
  SKR03: '1800',
  SKR04: '2100',
} as const;

/**
 * Ausgaben-Steuerschemata, die im MVP fail-closed in den Bucket fallen
 * (Doppel-Steuerwirkung / Sonderschlüssel nicht gate-freigegeben).
 *
 * WL-K4-Audit (2026-07-07, s. Modul-Dokumentation oben „Bewusst NICHT gesetzt"):
 * `reverse_charge_13b` (mehrere, nicht unterscheidbare §13b-Untertatbestands-BU-Schlüssel)
 * und `intra_community_6a` (kandidierende BU 17/18 ohne verifizierte Quelle in diesem Repo)
 * bleiben bewusst bestehen — ein falscher, aber plausibler Schlüssel wäre hier schlimmer
 * als der sichtbare Bucket. `import_third_country` (EUSt läuft über einen separaten
 * Zoll-Beleg, §15 Abs. 1 Nr. 2 UStG, kein BU-Schlüssel im Buchungsstapel dafür vorgesehen)
 * und `needs_review` (per Definition unklar) bleiben unverändert gebucketet.
 */
const EXPENSE_BUCKET_SCHEMES: ReadonlySet<TaxScheme> = new Set<TaxScheme>([
  'reverse_charge_13b',
  'intra_community_6a',
  'import_third_country',
  'needs_review',
]);

// ─── Erlöse (Ausgangsrechnungen) ────────────────────────────────────────────────

/**
 * Auflösung einer Position auf ihre fachliche Buchungs-Kategorie.
 *   - `standard`  → satzbasierte Logik (8400/8300, §19-0 %, BU erlaubt).
 *   - `mapped`    → eine explizite VB-1-Kategorie mit eigenem Automatik-Erlöskonto.
 *   - `bucket`    → fail-closed: nicht sicher mappbar (mehrdeutig/unbekannt).
 */
type RevenueSelectionResolution =
  | { kind: 'standard' }
  | { kind: 'mapped'; key: Exclude<InvoiceTaxCategoryKey, 'standard'> }
  | { kind: 'bucket' };

/**
 * Bestimmt die Buchungs-Kategorie einer Position aus dem präzisen `taxCategorySelection`
 * (Vorrang) bzw. — falls nur der BT-151-Code vorliegt — aus den eindeutigen Codes.
 *
 * Fail-closed: alles, was nicht eindeutig auf eine der 7 VB-1-Auswahlen abbildbar ist,
 * wird zu `bucket`. Insbesondere wird `E` (mehrdeutig §19/§4) ohne `taxCategorySelection`
 * NIE geraten.
 *
 * @param selectionKey       Präziser VB-1-Auswahl-Schlüssel der Position (oder null).
 * @param explicitTaxCategory EN-16931 BT-151-Code (Alt-/API-Pfad), oder null.
 */
function resolveRevenueSelection(
  selectionKey: InvoiceTaxCategoryKey | string | null | undefined,
  explicitTaxCategory: string | null | undefined,
): RevenueSelectionResolution {
  // (a) Präziser Auswahl-Schlüssel hat Vorrang — disambiguiert auch `E` (§19 vs. §4).
  const sk = (selectionKey ?? '').toString().trim();
  if (sk.length > 0) {
    if (sk === 'standard') return { kind: 'standard' };
    if (Object.prototype.hasOwnProperty.call(DATEV_VB1_REVENUE_ACCOUNTS, sk)) {
      return { kind: 'mapped', key: sk as Exclude<InvoiceTaxCategoryKey, 'standard'> };
    }
    // Unbekannter Key (OSS/§3c, §25a, §13b-Bau-Sub-Tatbestände, …) → fail-closed.
    return { kind: 'bucket' };
  }

  // (b) Kein Auswahl-Schlüssel: Rückfall auf den BT-151-Code für die EINDEUTIGEN Codes.
  const cat = (explicitTaxCategory ?? '').toString().trim().toUpperCase();
  if (cat.length === 0 || cat === 'S') {
    // Kein/Standard-Code → satzbasierte Logik (deckt §19 über `isSmallBusiness` ab).
    return { kind: 'standard' };
  }
  const inferred = BT151_CODE_TO_SELECTION[cat];
  if (inferred && inferred !== 'standard') {
    return { kind: 'mapped', key: inferred as Exclude<InvoiceTaxCategoryKey, 'standard'> };
  }
  // `E` (mehrdeutig) oder unbekannter Code → fail-closed Bucket. NIE §19/§4 raten.
  return { kind: 'bucket' };
}

/**
 * Mappt eine explizite VB-1-Auswahl (Nicht-Standard) auf ihr DATEV-Automatik-Erlöskonto.
 *
 * 🔴 Auflage 1: `buKey` ist IMMER `null` (Automatik-Konto, kein Steuerschlüssel).
 * 🔴 Auflage 2: `intra_community_6a` (IGL) nur mappen, wenn die Käufer-USt-IdNr. (BT-48)
 *    vorliegt — sonst fail-closed Bucket (`null`), damit eine ggf. steuerpflichtige
 *    Lieferung nicht versehentlich steuerfrei verbucht wird.
 *
 * @param key               VB-1-Auswahl (ohne `standard`).
 * @param skr               Gewählter Kontenrahmen.
 * @param buyerVatIdPresent Liegt die Käufer-USt-IdNr. (BT-48) auf der Rechnung vor?
 * @returns AccountMapping oder `null` (IGL-Gate nicht erfüllt).
 */
function mapVb1RevenueSelection(
  key: Exclude<InvoiceTaxCategoryKey, 'standard'>,
  skr: SkrKontenrahmen,
  buyerVatIdPresent: boolean,
): AccountMapping | null {
  if (key === 'intra_community_6a' && !buyerVatIdPresent) {
    return null; // Auflage 2: IGL ohne Erwerber-USt-IdNr. → fail-closed Bucket.
  }
  return {
    account: DATEV_VB1_REVENUE_ACCOUNTS[key][skr],
    buKey: null, // Auflage 1: Automatik-Konto — NIEMALS ein BU-Schlüssel.
    gateStatus: 'mvp-safe',
  };
}

/**
 * Liefert die Erlös-Kontierung für eine Ausgangsrechnungs-Position.
 *
 * Fail-closed: `null` ⇒ Bucket (§4.6). NIE ein geratenes Konto.
 *
 * Regeln:
 *   - `selectionKey` (präziser VB-1-Auswahl-Schlüssel) hat Vorrang; sonst Rückfall auf
 *     den eindeutigen BT-151-Code (`explicitTaxCategory`). `E` ohne Auswahl-Schlüssel
 *     ist mehrdeutig (§19/§4) → Bucket.
 *   - Explizite VB-1-Kategorien (Z/K/AE/G + §19/§4 via Key) → eigenes Automatik-Erlöskonto
 *     (8290/8195/8100/8125/8337/8120 bzw. SKR04), `buKey` IMMER null (Auflage 1).
 *   - IGL (`intra_community_6a`) nur mit Käufer-USt-IdNr., sonst Bucket (Auflage 2).
 *   - Standard (S / kein Code): rate 19 → 8400/4400, rate 7 → 8300/4300, je kein BU;
 *     rate 0 + `isSmallBusiness === true` → 8195/4185; rate 0 ohne §19 → Bucket;
 *     jeder andere Satz → Bucket (nur 19/7/0 sind MVP-safe).
 *
 * @param taxRate            USt-Satz der Position (19, 7, 0, …).
 * @param isSmallBusiness    Seller-Status §19 — 0 %-Trigger im Standard-Pfad (undefined → kein §19).
 * @param explicitTaxCategory EN-16931 BT-151-Code, falls vorhanden (API-/Altpfad), sonst null.
 * @param skr                Gewählter Kontenrahmen.
 * @param selectionKey       Präziser VB-1-Auswahl-Schlüssel der Position (optional, Vorrang).
 * @param buyerVatIdPresent  Liegt die Käufer-USt-IdNr. (BT-48) vor? (optional; IGL-Gate, fail-closed).
 * @returns AccountMapping oder `null` (Bucket).
 */
export function mapRevenueAccount(
  taxRate: number,
  isSmallBusiness: boolean,
  explicitTaxCategory: string | null,
  skr: SkrKontenrahmen,
  selectionKey?: InvoiceTaxCategoryKey | string | null,
  buyerVatIdPresent?: boolean,
): AccountMapping | null {
  const resolution = resolveRevenueSelection(selectionKey, explicitTaxCategory);

  if (resolution.kind === 'bucket') {
    return null; // fail-closed: nicht eindeutig mappbar.
  }
  if (resolution.kind === 'mapped') {
    return mapVb1RevenueSelection(resolution.key, skr, buyerVatIdPresent === true);
  }

  // resolution.kind === 'standard' → satzbasierte Logik (unverändert).
  const accounts = DATEV_REVENUE_ACCOUNTS[skr];

  if (taxRate === 19) {
    return { account: accounts.rate19, buKey: null, gateStatus: 'mvp-safe' };
  }
  if (taxRate === 7) {
    return { account: accounts.rate7, buKey: null, gateStatus: 'mvp-safe' };
  }
  if (taxRate === 0) {
    // Einziger eindeutiger 0 %-Fall im Standard-Pfad: §19 (fail-closed: undefined → kein §19).
    if (isSmallBusiness === true) {
      return { account: accounts.smallBusiness, buKey: null, gateStatus: 'mvp-safe' };
    }
    return null; // steuerfrei §4 / §6a-IGL / §13b-out / Ausfuhr — mehrdeutig → Bucket.
  }

  // Jeder andere Satz (16, 5.5, …) ist nicht MVP-safe → Bucket.
  return null;
}

// ─── Aufwand / Vorsteuer (Ausgaben) ─────────────────────────────────────────────

/**
 * Liefert die Aufwand-/Vorsteuer-Kontierung für eine Ausgabe.
 *
 * Fail-closed: `null` ⇒ Bucket (§4.6).
 *
 * Regeln:
 *   - taxScheme ∈ {§13b, IGL, Drittland, needs_review} → Bucket (Doppel-Steuerwirkung /
 *     Sonderschlüssel nicht gate-freigegeben).
 *   - Aufwandskonto = `skrAccountOverride` (Nutzer/KI) ODER Default 4980/6300.
 *   - BU-Schlüssel (A5-Kollisionsregel):
 *       • nutzergesetztes `skrAccount` → BU LEER (potenzielles Automatikkonto, kein Konflikt).
 *       • M7-Default-Konto (4980/6300) + standard + abziehbar → BU 9 (19 %) / BU 8 (7 %).
 *       • small_business_19 / zero_exempt → kein VSt → BU LEER.
 *
 * @param taxScheme         Steuerschema der Ausgabe (bereits fail-closed abgeleitet).
 * @param vatRate           USt-Satz (für BU-Wahl auf dem Default-Konto), oder null.
 * @param vatDeductible     Ob Vorsteuer abziehbar ist (steuert BU-Emission auf Default-Konto).
 * @param skrAccountOverride Vom Nutzer/KI gesetztes Aufwandskonto, oder null.
 * @param skr               Gewählter Kontenrahmen.
 * @returns AccountMapping oder `null` (Bucket).
 */
export function mapExpenseAccount(
  taxScheme: TaxScheme,
  vatRate: number | null,
  vatDeductible: boolean,
  skrAccountOverride: string | null,
  skr: SkrKontenrahmen,
): AccountMapping | null {
  if (EXPENSE_BUCKET_SCHEMES.has(taxScheme)) {
    return null; // §13b/IGL/Drittland/needs_review → Bucket.
  }

  const userAccount =
    typeof skrAccountOverride === 'string' && skrAccountOverride.trim().length > 0
      ? skrAccountOverride.trim()
      : null;

  // Nutzergesetztes Konto: KEIN BU-Schlüssel (A5 — Automatikkonto-Kollision vermeiden).
  if (userAccount !== null) {
    return { account: userAccount, buKey: null, gateStatus: 'mvp-safe' };
  }

  // M7-Default-Aufwandskonto (Nicht-Automatikkonto) → Vorsteuer über BU-Schlüssel.
  const defaultAccount = DATEV_DEFAULT_EXPENSE_ACCOUNT[skr];

  let buKey: string | null = null;
  if (taxScheme === 'standard' && vatDeductible) {
    if (vatRate === 19) {
      buKey = VORSTEUER_BU.rate19;
    } else if (vatRate === 7) {
      buKey = VORSTEUER_BU.rate7;
    }
    // andere/keine Rate → kein BU (Brutto auf Aufwand, kein VSt-Abzug).
  }
  // small_business_19 / zero_exempt → kein VSt → buKey bleibt null.

  return { account: defaultAccount, buKey, gateStatus: 'mvp-safe' };
}

// ─── Reisekosten (RK-5, additiv — architecture-reisekosten.md §3.4) ────────────

/**
 * Reisekosten-Sachkonten (Unternehmer-Selbstabrechnung), Pauschalen (kein Ist-Kosten-Pfad).
 * Quelle: DATEV-Recherche §3.1. `verpflegungPauschale` (4674/6674) und `fahrtkosten`
 * (4673/6673, trägt auch die km-Pauschale) sind beide ✅ VERIFIZIERT.
 *
 * Bewusst NICHT enthalten (MVP): `uebernachtung` (4676/6680) und `nicht_abziehbar`
 * (4672/6672) — beide ⚠️ unverifiziert und im MVP ungenutzt, da Ist-Kosten (Hotel/Bahn/Taxi)
 * unverändert über den bestehenden `mapExpenseAccount`-Weg laufen (Nutzer-`skrAccount` oder
 * Default 4980/6300). Dedizierte Ist-Kosten-Reisekonten sind Folgewelle RK-W2-6.
 */
export const DATEV_REISEKOSTEN_ACCOUNTS: Readonly<
  Record<SkrKontenrahmen, { verpflegungPauschale: string; fahrtkosten: string }>
> = {
  SKR03: { verpflegungPauschale: '4674', fahrtkosten: '4673' },
  SKR04: { verpflegungPauschale: '6674', fahrtkosten: '6673' },
} as const;

/**
 * Privateinlage-Gegenkonto für beleglose Reisekosten-Pauschalen (kein Geldabfluss,
 * Eigenkapital-Zuführung).
 *
 * ⚠️ zu verifizieren (O-1, W1) — vor Flag-Flip gegen amtlichen DATEV-Kontenrahmen prüfen;
 * kein weiteres Raten.
 */
export const DATEV_PRIVATEINLAGE_ACCOUNTS: Readonly<Record<SkrKontenrahmen, string>> = {
  SKR03: '1890',
  SKR04: '2180',
} as const;

/** Reisekosten-Pauschalen-Art (nur beleglose Pauschalen — keine Ist-Kosten). */
export type ReisekostenKind = 'verpflegung_pauschale' | 'km_pauschale';

/**
 * Kontierung einer Reisekosten-PAUSCHALE (Verpflegung oder km-Geld).
 *
 * ❌ V1-Leitplanke (§15 UStG): `buKey` ist im Rückgabetyp LITERAL `null` — Typ-Ebene
 * erzwungen, nicht nur zur Laufzeit. Eine beleglose Pauschale hat NIEMALS eine
 * abziehbare Vorsteuer; es darf hier NIE ein Vorsteuer-Schlüssel gesetzt werden
 * (anders als `mapExpenseAccount`, das für Ist-Kosten mit Beleg BU 8/9 vergeben darf).
 *
 * @param kind 'verpflegung_pauschale' → Verpflegungspauschale-Konto; 'km_pauschale' → Fahrtkosten-Konto.
 * @param skr  Gewählter Kontenrahmen.
 * @returns Kontierung mit `buKey: null` (immer) und `gateStatus: 'mvp-safe'` (immer — beide Konten sind ✅ verifiziert).
 */
export function mapReisekostenAccount(
  kind: ReisekostenKind,
  skr: SkrKontenrahmen,
): { account: string; buKey: null; gateStatus: 'mvp-safe' } {
  const accounts = DATEV_REISEKOSTEN_ACCOUNTS[skr];
  const account = kind === 'verpflegung_pauschale' ? accounts.verpflegungPauschale : accounts.fahrtkosten;
  return { account, buKey: null, gateStatus: 'mvp-safe' };
}
